<template>
  <v-container fluid>
    <!-- Faculty selector -->
    <v-row justify="center">
      <v-col cols="12" sm="6" md="5">
        <v-autocomplete
          color="#0F2027"
          dense rounded required
          v-model="selectedFaculties"
          :items="facultyList"
          item-text="name" item-value="id"
          label="Select Faculties"
          outlined persistent-hint
          hint="Select one or more faculties"
          multiple chips deletable-chips
          @change="fetchLeaveAnalytics"
        />
      </v-col>
    </v-row>

    <!-- Charts – one block per selected faculty -->
    <div v-if="leaveDataLoaded">
  <v-row v-for="faculty in groupedLeaveAnalytics" :key="faculty.facultyId">
    <v-col cols="12">
<v-card class="mb-10 pa-4" elevation="4">        
          <!-- One row per leave-type inside the faculty -->
          <v-row v-for="lt in faculty.leaveTypes.filter(hasLeaveData)" :key="lt.leaveType" class="mb-8">
  <!-- Faculty and Leave Type Header -->
  <v-col cols="12" class="text-center mb-4">
    <span class="font-weight-bold text-subtitle-1">
      Faculty:
      {{ faculty.facultyCode ? faculty.facultyCode + ' – ' : '' }}
      {{ faculty.facultyName }}
      |
      Leave Type: {{ lt.leaveType }}
    </span>
  </v-col>

  <!-- Chart + Summary Boxes Side by Side -->
  <v-col cols="12" md="9" class="d-flex justify-center align-center">
    <div 
      class="chart-container"
      style="
        width: 100%; 
        max-width: 600px; 
        height: 480px; 
        min-height: 400px;
        border: 1px solid #ccc; 
        border-radius: 12px;
        box-shadow: 0 4px 20px rgba(0,0,0,0.08);
        background: #fff;
      "
    >
      <chart
        :ref="`chart_${faculty.facultyId}_${lt.leaveType}`"
        style="width:100%; height:100%;"
        auto-resize
        :options="getLeaveChartOptions(lt)"
        :key="`${faculty.facultyId}-${lt.leaveType}-chart`"
      />
    </div>
  </v-col>

  <!-- Summary Squares -->
  <v-col cols="12" md="3" class="d-flex flex-column justify-center align-center align-md-start">
    <div style="width: 100%; max-width: 300px;">
      <!-- TOTAL -->
      <div class="squares mb-4" 
           style="background:#40b8e6; border:4px solid #80d4f6; color:#ffffff;">
        <b>TOTAL</b> <b>{{ lt.total }}</b>
      </div>

      <!-- IN-PROCESS -->
<div class="squares mb-4" 
     style="background:#ffeb3b; border:4px solid #fff176; color:#212121;">
  <b>IN-PROCESS</b> <b>{{ lt.inProgress }}</b>
</div>

      <!-- AVAILED -->
      <div class="squares mb-4" 
           style="background:#f54a94; border:4px solid #fa8bb9; color:#ffffff;">
        <b>AVAILED</b> <b>{{ lt.availed }}</b>
      </div>

      <!-- BALANCE -->
      <div class="squares" 
           style="background:#73d0a0; border:4px solid #a0e6c2; color:#212121;">
        <b>BALANCE</b> <b>{{ lt.balance }}</b>
      </div>
    </div>
  </v-col>
</v-row>
      </v-card>
    </v-col>
  </v-row>

  <!-- No data -->
  <v-alert v-if="groupedLeaveAnalytics.length === 0" type="warning" class="mt-4">
    No leave data for the selected faculties.
  </v-alert>
</div>


    <v-snackbar v-model="snackbar" :color="color" :timeout="3000">
      {{ snackbar_msg }}
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from "axios";

export default {
  name: "LeaveDashboard",
  props: [
    'selected_ay',
    'selected_semester',
    'selected_standard',
    'selected_div',
    'selected_course',
    'selected_learner',
    'render1',
  ],
  data() {
    return {
      selectedFaculties: [],
      selectedAy: null,
      facultyList: [],
      ayList: [],
      leaveTypeList: [],
      leaveAnalytics: [],
      groupedLeaveAnalytics: [],
      leaveDataLoaded: false,
      snackbar: false,
      color: "",
      snackbar_msg: "",
    };
  },
  computed: {
    getAcademicYearName() {
      const ay = this.ayList.find(item => item.id === this.selectedAy);
      return ay ? ay.ay : this.selectedAy;
    },
  },
  watch: {
   selectedFaculties(newVal, oldVal) {
    // Case 1: "ALL" selected → keep only ALL
    if (newVal.includes('ALL') && oldVal && !oldVal.includes('ALL')) {
      this.selectedFaculties = ['ALL'];
      return;
    }

    // Case 2: Individual selected while ALL is already selected
    if (oldVal && oldVal.includes('ALL') && newVal.length > 1) {
      this.selectedFaculties = newVal.filter(id => id !== 'ALL');
      return;
    }
  },
    render1() {
      if (this.render1) this.fetchLeaveAnalytics();
    },
  },
  mounted() {
    this.onload();
    window.addEventListener('resize', this.resizeAllCharts);
  },
  methods: {
    hasLeaveData(lt) {
    return (
      Number(lt.total)      > 0 ||
      Number(lt.inProgress) > 0 ||
      Number(lt.availed)    > 0 ||
      Number(lt.balance)    > 0
    );
  },
    showSnackbar(clr, msg) {
      this.snackbar = true;
      this.color = clr;
      this.snackbar_msg = msg;
    },
    async onload() {
  try {
    const r = await axios.post('/AdminDashboard/instructorLeaveAnalyticsAdmin', {
      orgId: this.$route.params.orgId,
      user: this.$store.getters.user,
      facultyIds: [],
      academicYearId: null,
      standardIds: this.selected_standard ? [this.selected_standard] : [],
      divisionIds: this.selected_div ? [this.selected_div] : [],
    });

    if (r.data.flag !== "YES") throw new Error("No data");

    // Prepend the 'All Faculties' option
    this.facultyList = [
      { id: 'ALL', name: 'All' },
      ...(r.data.faculty_list || [])
    ];

    this.ayList = r.data.ay_list || [];
    this.leaveTypeList = r.data.leave_type_list || [];

    // pre-select first (All Faculties) and active AY
    if (!this.selectedFaculties.length) {
      this.selectedFaculties = ['ALL'];
    }

    if (this.ayList.length && !this.selectedAy) {
      const active = this.ayList.find(a => a.isactive);
      this.selectedAy = active ? active.id : this.ayList[0].id;
    }

    this.fetchLeaveAnalytics();
  } catch (e) {
    console.error(e);
    this.showSnackbar("#b71c1c", "Failed to load initial data");
  }
},
  
async fetchLeaveAnalytics() {
  if (!this.selectedFaculties.length || !this.selectedAy) {
    this.showSnackbar("#b71c1c", "Select at least one faculty");
    return;
  }

  this.leaveDataLoaded = false;
  this.groupedLeaveAnalytics = [];

  try {
    // prepare faculty IDs to send
    const isAll = this.selectedFaculties.includes('ALL');
    const selectedIds = isAll
      ? this.facultyList.filter(f => f.id !== 'ALL').map(f => f.id)
      : this.selectedFaculties;

    const r = await axios.post('/AdminDashboard/instructorLeaveAnalyticsAdmin', {
      orgId: this.$route.params.orgId,
      user: this.$store.getters.user,
      facultyIds: selectedIds,
      academicYearId: this.selectedAy,
      standardIds: this.selected_standard ? [this.selected_standard] : [],
      divisionIds: this.selected_div ? [this.selected_div] : [],
    });

    const raw = r.data.aggregated_leave_data || [];

    if (!raw.length) {
      this.groupedLeaveAnalytics = [];
      this.leaveDataLoaded = true;
      return;
    }

    if (isAll) {
      const combined = {};

      raw.forEach(row => {
        const lt = row.leaveType;
        if (!combined[lt]) {
          combined[lt] = {
            total: 0,
            inProgress: 0,
            availed: 0,
            balance: 0  
          };
        }
        combined[lt].total += Number(row.total) || 0;
        combined[lt].inProgress += Number(row.inProgress) || 0;
        combined[lt].availed += Number(row.availed) || 0;
      });

      Object.keys(combined).forEach(lt => {
        const item = combined[lt];
        item.balance = item.total - item.availed - item.inProgress;
        if (item.balance < 0) item.balance = 0;
      });

      this.groupedLeaveAnalytics = [{
        facultyId: 'ALL',
        facultyName: 'All Faculties',
        facultyCode: '',
        leaveTypes: Object.keys(combined).map(lt => ({
          leaveType: lt,
          total: combined[lt].total,
          inProgress: combined[lt].inProgress,
          availed: combined[lt].availed,
          balance: combined[lt].balance
        }))
      }];

      const uniqueInstructors = new Set(raw.map(r => r.instructorId)).size;
      console.log(`"All Faculties" aggregates data from ${uniqueInstructors} instructors`);
    }
    else {
      const map = {};

      raw.forEach(row => {
        // Filter only the selected faculty data (important fix)
        if (!selectedIds.includes(row.instructorId)) return;

        const fid = row.instructorId;
        if (!map[fid]) {
          const fac = this.facultyList.find(f => f.id === fid);
          map[fid] = {
            facultyId: fid,
            facultyName: fac ? fac.name : 'Unknown',
            leaveTypes: [],
          };
        }

        map[fid].leaveTypes.push({
          leaveType: row.leaveType,
          total: Number(row.total) || 0,
          inProgress: Number(row.inProgress) || 0,
          availed: Number(row.availed) || 0,
          balance: Number(row.balance) || 0,
        });
      });

      this.groupedLeaveAnalytics = Object.values(map);
    }

    this.leaveDataLoaded = true;
    this.$nextTick(() => {
      this.resizeAllCharts();
    });
  } catch (e) {
    console.error(e);
    this.showSnackbar("#b71c1c", "Failed to load analytics");
  }
},
resizeAllCharts() {
    this.groupedLeaveAnalytics.forEach(faculty => {
      faculty.leaveTypes
        .filter(this.hasLeaveData)
        .forEach(lt => {
          const refKey = `chart_${faculty.facultyId}_${lt.leaveType}`;
          const chartInstance = this.$refs[refKey];
          if (chartInstance && chartInstance[0] && typeof chartInstance[0].resize === 'function') {
            chartInstance[0].resize();
          }
        });
    });
  },
  beforeDestroy() {
  window.removeEventListener('resize', this.resizeAllCharts);
},
    processLeaveData(transactions, leaveTypes) {
      const analytics = [];
      this.selectedFaculties.forEach(facultyId => {
        leaveTypes.forEach(leaveType => {
          const facultyTransactions = transactions.filter(t => 
            t.instructorId === facultyId && 
            t.leavetype === leaveType.type && 
            t.leaveacademicyear_id === this.selectedAy
          );
          
          const total = facultyTransactions.reduce((sum, t) => sum + (Number(t.totalleaves) || 0), 0);
          const inProgress = facultyTransactions.reduce((sum, t) => sum + (Number(t.leavesinprogress) || 0), 0);
          const availed = facultyTransactions.reduce((sum, t) => sum + (Number(t.availedleaves) || 0), 0);
          const balance = facultyTransactions.reduce((sum, t) => sum + (Number(t.balanceleaves) || 0), 0);

          console.log(`Processing leaveType ${leaveType.type} for faculty ${facultyId} and AY ${this.selectedAy}: total=${total}, inProgress=${inProgress}, availed=${availed}, balance=${balance}`);

          analytics.push({
            facultyId,
            leaveType: leaveType.type,
            total,
            inProgress,
            availed,
            balance,
          });
        });
      });
      return analytics;
    },
    processAggregatedData(aggregatedData, leaveTypes) {
      const analytics = [];
      this.selectedFaculties.forEach(facultyId => {
        leaveTypes.forEach(leaveType => {
          const facultyAggData = aggregatedData.find(a => 
            a.instructorId === facultyId && 
            a.leaveType === leaveType.type && 
            a.leaveacademicyear_id === this.selectedAy
          ) || { total: 0, inProgress: 0, availed: 0, balance: 0 };

          console.log(`Processing aggregated leaveType ${leaveType.type} for faculty ${facultyId} and AY ${this.selectedAy}: total=${facultyAggData.total}, inProgress=${facultyAggData.inProgress}, availed=${facultyAggData.availed}, balance=${facultyAggData.balance}`);

          analytics.push({
            facultyId,
            leaveType: leaveType.type,
            total: facultyAggData.total,
            inProgress: facultyAggData.inProgress,
            availed: facultyAggData.availed,
            balance: facultyAggData.balance,
          });
        });
      });
      return analytics;
    },
    groupByFacultyAndLeaveType(analytics) {
      const grouped = [];
      analytics.forEach(item => {
        const faculty = this.facultyList.find(f => f.id === item.facultyId);
        grouped.push({
          ...item,
          facultyName: faculty ? faculty.name : 'Unknown',
        });
      });
      return grouped;
    },
    getLeaveChartOptions(leaveRow) {
 const colorMap = {
 'Total':      '#40b8e6',  // darker cyan
    'In-Process': '#ffeb3b',  // darker purple
    'Availed':    '#f54a94',  // darker pink
    'Balance':    '#73d0a0'
};

  const data = [
    { value: leaveRow.total,      name: "Total",      itemStyle: { color: colorMap['Total'] } },
    { value: leaveRow.inProgress, name: "In-Process", itemStyle: { color: colorMap['In-Process'] } },
    { value: leaveRow.availed,    name: "Availed",    itemStyle: { color: colorMap['Availed'] } },
    { value: leaveRow.balance,    name: "Balance",    itemStyle: { color: colorMap['Balance'] } }
  ].filter(d => d.value > 0);

  return {
    tooltip: { trigger: "item", formatter: "{a}<br/>{b}: {c} days ({d}%)" },
    series: [{
      name: leaveRow.leaveType,
      type: "pie",
      radius: "55%",
      data: data,
      label: {
        show: true,
        position: 'outside',     // Shows labels outside the pie
        formatter: '{b}\n{c} days', 
        fontSize: 15.5,            // Larger label font
        fontWeight: 'bold',
        color: '#333'
      },
      labelLine: {
        show: true,
        length: 20,
        length2: 10
      },
      emphasis: {
        itemStyle: {
          shadowBlur: 10,
          shadowOffsetX: 0,
          shadowColor: "rgba(0,0,0,0.1)"
        }
      }
    }]
  };
},
  },
};
</script>

<style scoped>
.box {
  margin: 10px;
  padding: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  width: 100%;
}

.toolbar {
  background-color: #1976d2;
  color: white;
  font-size: 14px;
}

.squares {
  height: 85px;                 /* Reduced from 70px */
  width: 100%;
  max-width: 180px;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  font-size: 13px;
  box-shadow: 0 3px 8px rgba(0,0,0,0.1);
}

.squares b {
  font-size: 18px;
  font-weight: bold;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .squares {
    max-width: 120px; /* Further reduced for small screens */
    height: 50px; /* Further reduced height */
  }

  .squares b {
    font-size: 12px; /* Adjusted for small screens */
  }
}

@media (min-width: 601px) and (max-width: 960px) {
  .squares {
    max-width: 140px; /* Adjusted for medium screens */
    height: 55px; /* Adjusted height */
  }
}
</style>